import { Component, computed, inject, input, signal } from '@angular/core';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { TranslocoPipe } from '@jsverse/transloco';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { NgSelectModule } from '@ng-select/ng-select';
import { FontawesomeService } from '../../../services/fontawesome.service';
import { ReleaseNotesModalService } from '../../../services/release-notes-modal.service';
import { Store } from '@ngrx/store';
import { selectUserAccount } from '../../../store/user-account/user-account.reducer';
import { AppState } from '../../../app.reducer';
import { Language, LanguageService, ThemingService, languages } from '@catch/services';
import { logout } from '../../../store/auth/auth.actions';
import { hasPermission } from '../../../store/auth/auth.reducer';
import { CompanyUserRole } from '../../../enums/company-user.role.enum';
import { UserAdapter } from '../../../adapters/user.adapter';

type PopoverCloseEvent = Pick<Event, 'preventDefault' | 'stopPropagation'>;

@Component({
  selector: 'app-sidenav-user',
  template: `
  <div class="ct-card p-0">
    <ul class="dropdown-menu">
      <li [routerLink]="['/settings', 'profile']" routerLinkActive="active" (click)="dismissPopover($event)">
        <fa-icon
          [icon]="fontawesomeService.light.userCog"
        ></fa-icon>
        <p>{{ 'company-tool.topnav.profile' | transloco }}</p>
      </li>
      @if (isAdmin()) {
        <li [routerLink]="['/settings', 'company']" routerLinkActive="active" (click)="dismissPopover($event)">
          <fa-icon
            [icon]="fontawesomeService.light.cogs"
          ></fa-icon>
          <p>{{ 'company-tool.topnav.company' | transloco }}</p>
        </li>
      } @else {
        <li
          class="disabled"
          position="left"
          (click)="dismissPopover($event)"
          [tooltip]="'company-tool.sidenav.admin-only' | transloco"
        >
          <fa-icon
            [icon]="fontawesomeService.light.cogs"
          ></fa-icon>
          <p>{{ 'company-tool.topnav.company' | transloco }}</p>
        </li>
      }
      <hr />
      <li (click)="openReleaseNotes($event)">
        <fa-icon
          [icon]="fontawesomeService.light.commentAltEdit"
        ></fa-icon>
        <p>{{ 'company-tool.topnav.release-notes' | transloco }}</p>
      </li>
      <li>
        <fa-icon [icon]="fontawesomeService.light.globe"></fa-icon>
        <ng-select
          [placeholder]="'company-tool.topnav.language' | transloco"
          [items]="languages()"
          [bindLabel]="'name'"
          (change)="setLanguage($event.value)"
          [searchable]="false"
          [clearable]="false"
        >
        </ng-select>
      </li>
      <li (click)="themingService.toggleTheme()">
        @switch (themingService.mode) {
          @case ('auto') {
            <fa-icon [icon]="fontawesomeService.light.display"></fa-icon>
          }
          @case ('light') {
            <fa-icon [icon]="fontawesomeService.light.brightnessLow"></fa-icon>
          }
          @case ('dark') {
            <fa-icon [icon]="fontawesomeService.light.moon"></fa-icon>
          }
        }
        <p>{{ 'company-tool.topnav.appearance' | transloco }}</p>
      </li>
      <hr />
      <li (click)="dismissPopover($event)">
        <a href="https://knowledge.catch.hr/" target="_blank">
          <fa-icon [icon]="fontawesomeService.light.book"></fa-icon>
          <p>
            {{ 'company-tool.topnav.knowledge-base' | transloco }}
            <fa-icon [icon]="fontawesomeService.light.externalLink"></fa-icon>
          </p>
        </a>
      </li>
      <hr />
      <li (click)="logout($event)">
        <fa-icon
          [icon]="fontawesomeService.light.signOutAlt"
        ></fa-icon>
        <p>{{ 'company-tool.topnav.logout' | transloco }}</p>
      </li>
    </ul>
  </div>
  `,
  styles: [`
    
  `],
  imports: [FontAwesomeModule, TranslocoPipe, RouterModule, NgSelectModule],
})
export class SidenavUserComponent {
  readonly closePopover = input<((event: PopoverCloseEvent) => void) | undefined>();
  
  public readonly fontawesomeService = inject(FontawesomeService);
  public readonly themingService = inject(ThemingService);
  public readonly store = inject(Store<AppState>);
  public readonly router = inject(Router);
  public readonly activatedRoute = inject(ActivatedRoute);
  public readonly userAccount = this.store.selectSignal(selectUserAccount);
  private readonly languageService = inject(LanguageService);
  private readonly userAdapter = inject(UserAdapter);
  private readonly releaseNotesModalService = inject(ReleaseNotesModalService);

  public isAdmin = this.store.selectSignal(hasPermission(CompanyUserRole.ADMIN));
  public readonly languages = signal<Language[]>(languages.filter((lang) => !lang.value.includes('fr')));

  /**
   * Logout the user
   */
  public logout(event?: Event): void {
    this.dismissPopover(event);
    this.store.dispatch(logout());
  }

  public openReleaseNotes(event?: Event): void {
    this.dismissPopover(event);
    this.releaseNotesModalService.open();
  }
  
  
  /**
   * Set the language of the user
   * @param value The language to set
   */
  public setLanguage(value: string): void {
    this.languageService.setLanguage(value).then(() => {
      this.userAdapter.setLanguage().subscribe();
    });
  }

  dismissPopover(event?: Event): void {
    const closeEvent = event ?? this.createFallbackCloseEvent();
    this.closePopover()?.(closeEvent);
  }

  private createFallbackCloseEvent(): PopoverCloseEvent {
    return {
      preventDefault: () => undefined,
      stopPropagation: () => undefined,
    };
  }
}
